import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { t } from 'ttag';
import useGroupContext from '../../../../../contexts/group/useGroupContext';
import { CTA } from '../../../../general/Button';
import TinyModal from '../../../../general/TinyModal';
import GroupNameForm from './GroupNameForm';

type GroupJoinModalProps = {
    // the group order in the link, if there is one
    token: string | null;
    // Cancel: where to go instead
    onCancel: () => void;
};

// What guests meet on opening the link: who's hosting, and a name to put to what they add
const GroupJoinModal = ({ token, onCancel }: GroupJoinModalProps) => {
    const groupContext = useGroupContext();
    const [joining, setJoining] = useState(false);

    useEffect(() => {
        if (token && groupContext.session?.token !== token) groupContext.fetchPreview(token);
    }, [token]);

    // (in it already: the ended modal says if it's over)
    if (!token || groupContext.session?.token === token) return null;

    const host = groupContext.preview?.host;

    const join = async (name: string) => {
        setJoining(true);

        try {
            await groupContext.join(token, name);
        } catch (error) {
            // (the API said why, and the preview will say it's gone)
            groupContext.fetchPreview(token);
        } finally {
            setJoining(false);
        }
    };

    // a link to something that's over, or never was
    if (groupContext.previewFailed) {
        const placed = groupContext.preview?.group.status === 'placed';

        return (
            <TinyModal
                show={true}
                onHide={onCancel}
                modalID="group-join-modal"
                header={placed ? t`This order has been placed` : t`This group order has ended`}
                footer={
                    <CTA type="button" buddies onClick={onCancel}>
                        <span>{t`Go to the menu`}</span>
                    </CTA>
                }
            >
                <p>
                    {placed
                        ? t`The group order has already been placed, so items can't be added any more.`
                        : t`The link you were sent isn't working any more. Ask whoever sent it for a new link.`}
                </p>
            </TinyModal>
        );
    }

    // (nothing to say until we know who's hosting)
    if (!groupContext.preview) return null;

    const locked = groupContext.preview.group.status === 'locked';

    return (
        <TinyModal
            show={true}
            onHide={onCancel}
            modalID="group-join-modal"
            header={host ? t`Join ${host}'s order` : t`Join the group order`}
        >
            <>
                <p>
                    {locked
                        ? t`${host ?? 'The host'} is choosing a time and checking out. You can join now and add items if they go back to the menu.`
                        : t`Add your favourite dishes to the basket. ${host ?? 'The host'} will check out and pay for the whole order.`}
                </p>
                <Details>
                    <h3>{t`Order details`}</h3>
                    <div>
                        <span className="material-symbols-outlined">storefront</span>
                        <span>{t`Order from ${groupContext.preview.group.site_name}`}</span>
                    </div>
                    <div>
                        <span className="material-symbols-outlined">credit_card</span>
                        <span>{host ? t`It will be paid by ${host}` : t`It will be paid by the host`}</span>
                    </div>
                </Details>
                <h3>{t`Your details`}</h3>
                <GroupNameForm id="group-join-name" onSubmit={join} busy={joining}>
                    {(submit) => (
                        <>
                            <CTA type="button" secondary buddies onClick={onCancel}>
                                <span>{t`Cancel`}</span>
                            </CTA>
                            <CTA type="button" buddies loading={joining} onClick={submit}>
                                <span>{t`Continue`}</span>
                            </CTA>
                        </>
                    )}
                </GroupNameForm>
            </>
        </TinyModal>
    );
};

const Details = styled.div`
    margin-bottom: 16px;

    h3 {
        margin-bottom: 8px;
    }

    > div {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 4px 0;
    }
`;

export default GroupJoinModal;
